(() => {
  const DAYS = ['sun','mon','tue','wed','thu','fri','sat'];
  const MINUTE = 60000;

  const coords = card => {
    const lat = Number(card.dataset.lat);
    const lng = Number(card.dataset.lng);

    return Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180 &&
      card.dataset.lat.trim() !== '' &&
      card.dataset.lng.trim() !== ''
      ? [lat, lng]
      : null;
  };

  const distance = (a, b) => {
    const rad = Math.PI / 180;
    const dlat = (b[0] - a[0]) * rad;
    const dlng = (b[1] - a[1]) * rad;
    const h = Math.sin(dlat / 2) ** 2 +
      Math.cos(a[0] * rad) *
      Math.cos(b[0] * rad) *
      Math.sin(dlng / 2) ** 2;

    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  };

  function init(root) {
    if (root.dataset.locatorReady) return;
    root.dataset.locatorReady = 'true';

    const cards = [...root.querySelectorAll('.sl__card')];
    const list = root.querySelector('[data-cards]');
    const search = root.querySelector('[data-search]');
    const openBtn = root.querySelector('[data-open]');
    const nearestBtn = root.querySelector('[data-nearest]');
    const count = root.querySelector('[data-count]');
    const message = root.querySelector('[data-message]');
    const empty = root.querySelector('[data-empty]');

    const timezone = root.dataset.timezone || 'America/Panama';
    let map;
    let markers = new Map();
    let openOnly = false;
    let nearest = false;
    let userCoords = null;
    let active = null;
    let formatter;

    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      });
    } catch (e) {
      message.textContent = 'Zona horaria no válida; revisa la configuración.';
    }

    function localNow() {
      if (!formatter) return null;

      const parts = Object.fromEntries(
        formatter.formatToParts(new Date()).map(p => [p.type, p.value])
      );
      const index = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        .indexOf(parts.weekday);

      return {
        day: index,
        minutes: Number(parts.hour) * 60 + Number(parts.minute)
      };
    }

    function parseHours(value) {
      const match = /^(\d{2}):(\d{2})\s*-\s*(\d{2}):(\d{2})$/
        .exec((value || '').trim());

      if (!match) return null;

      const [h1, m1, h2, m2] = match.slice(1).map(Number);

      if (h1 > 23 || h2 > 23 || m1 > 59 || m2 > 59) return null;

      return [h1 * 60 + m1, h2 * 60 + m2];
    }

    function isOpen(card, now) {
      if (!now) return null;

      const today = parseHours(card.dataset[DAYS[now.day]]);
      const previous = parseHours(card.dataset[DAYS[(now.day + 6) % 7]]);

      if (
        today &&
        today[0] !== today[1] &&
        (
          today[1] > today[0]
            ? now.minutes >= today[0] && now.minutes < today[1]
            : now.minutes >= today[0]
        )
      ) {
        return true;
      }

      if (previous && previous[1] < previous[0] && now.minutes < previous[1]) {
        return true;
      }

      return false;
    }

    function status(card, now) {
      const el = card.querySelector('[data-status]');
      const hasHours = DAYS.some(day => parseHours(card.dataset[day]));
      const value = hasHours ? isOpen(card, now) : null;

      el.textContent = value === null
        ? 'Horario no disponible'
        : value
          ? 'Abierto ahora'
          : 'Cerrado ahora';

      el.classList.toggle('is-open', value === true);
      el.classList.toggle('is-closed', value === false);

      return value;
    }

    function visibleCards() {
      return cards.filter(card => !card.hidden);
    }

    function select(card, fromMap = false) {
      if (active) active.classList.remove('is-active');

      active = card;
      card.classList.add('is-active');

      const mobile = matchMedia('(max-width:650px)').matches;

      if (mobile && !fromMap) setView('map');
      if (!fromMap && !mobile) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      const point = coords(card);

      if (map && point) {
        const focus = () => {
          map.invalidateSize();

          markers.forEach((marker, branch) => {
            marker.setOpacity(branch === card ? 1 : 0);
          });

          map.setView(point, Math.max(map.getZoom(), 14), { animate: false });

          if (mobile) {
            map.panBy([0, -Math.min(90, map.getSize().y / 4)], { animate: false });
          }

          if (!fromMap) markers.get(card)?.openPopup();
        };

        if (mobile && !fromMap) {
          setTimeout(focus, 90);
        } else {
          focus();
        }
      }
    }

    function update() {
      const now = localNow();
      const query = search.value.trim().toLocaleLowerCase();

      cards.forEach(card => {
        const searchable = [
          card.querySelector('h3')?.textContent,
          card.querySelector('small')?.textContent,
          card.querySelector('address')?.textContent
        ].join(' ').toLocaleLowerCase();

        card.hidden = !(
          searchable.includes(query) &&
          (!openOnly || status(card, now) === true)
        );

        if (!openOnly) status(card, now);

        markers.get(card)?.setOpacity(
          card.hidden || (active && active !== card) ? 0 : 1
        );
      });

      const visible = visibleCards();

      if (nearest && userCoords) {
        visible.sort((a, b) =>
          distance(userCoords, coords(a) || [90, 180]) -
          distance(userCoords, coords(b) || [90, 180])
        );
      }

      visible.forEach(card => list.appendChild(card));
      count.textContent = visible.length;
      empty.hidden = visible.length !== 0;

      if (active?.hidden) {
        active.classList.remove('is-active');
        active = null;

        markers.forEach((marker, branch) => {
          marker.setOpacity(branch.hidden ? 0 : 1);
        });
      }
    }

    function setView(view) {
      root.dataset.mobileView = view;

      root.querySelectorAll('[data-view]').forEach(button => {
        button.setAttribute(
          'aria-pressed',
          String(button.dataset.view === view)
        );
      });

      if (view === 'map' && map) {
        setTimeout(() => map.invalidateSize(), 50);
      }
    }

    root.querySelectorAll('[data-view]').forEach(button => {
      button.addEventListener('click', () => setView(button.dataset.view));
    });

    setView('list');

    /* Ventana del teléfono en escritorio. En móvil se conserva el enlace tel:. */
    const phoneDialog = document.createElement('dialog');
    phoneDialog.className = 'sl__phone-dialog';
    phoneDialog.innerHTML =
      '<div class="sl__phone-dialog-content">' +
        '<strong>Teléfono de la sucursal</strong>' +
        '<p class="sl__phone-number"></p>' +
        '<button type="button" class="sl__phone-close">Cerrar</button>' +
      '</div>';

    root.appendChild(phoneDialog);

    phoneDialog.querySelector('.sl__phone-close')
      .addEventListener('click', () => phoneDialog.close());

    root.addEventListener('click', event => {
      const link = event.target.closest?.('a[href^="tel:"]');

      if (!link || !root.contains(link)) return;
      if (window.matchMedia('(max-width:650px)').matches) return;

      event.preventDefault();

      phoneDialog.querySelector('.sl__phone-number').textContent =
        link.getAttribute('href').replace(/^tel:/i, '');

      phoneDialog.showModal();
    });

    const searchInput = search;

    searchInput.addEventListener('input', update);

    openBtn.addEventListener('click', () => {
      openOnly = !openOnly;
      openBtn.setAttribute('aria-pressed', String(openOnly));
      update();
    });

    nearestBtn.addEventListener('click', () => {
      if (nearest) {
        nearest = false;
        nearestBtn.setAttribute('aria-pressed', 'false');
        message.textContent = '';
        update();
        return;
      }

      if (!navigator.geolocation) {
        message.textContent = 'Geolocalización no disponible.';
        return;
      }

      message.textContent = 'Solicitando ubicación…';

      navigator.geolocation.getCurrentPosition(
        pos => {
          userCoords = [pos.coords.latitude, pos.coords.longitude];
          nearest = true;
          nearestBtn.setAttribute('aria-pressed', 'true');
          message.textContent = 'Ordenadas por distancia aproximada.';
          update();
        },
        () => {
          message.textContent =
            'No se pudo obtener tu ubicación. Comprueba el permiso del navegador.';
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    });

    cards.forEach(card => {
      card.tabIndex = 0;
      card.setAttribute(
        'aria-label',
        `Ver ${card.querySelector('h3')?.textContent || 'sucursal'} en el mapa`
      );

      card.addEventListener('click', event => {
        if (event.target.closest('a,button')) return;
        select(card);
      });

      card.addEventListener('keydown', event => {
        if (event.target !== card) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select(card);
        }
      });

      card.querySelector('[data-focus]')
        .addEventListener('click', () => select(card));
    });

    function mountMap() {
      if (root.dataset.locatorReady !== 'true') return;

      if (!window.L) {
        root.querySelector('[data-map-error]').hidden = false;
        return;
      }

      const fallback = [
        Number(root.dataset.defaultLat),
        Number(root.dataset.defaultLng)
      ];

      const center = Number.isFinite(fallback[0]) &&
        Number.isFinite(fallback[1])
        ? fallback
        : [8.9824, -79.5199];

      map = L.map(root.querySelector('[data-map]'), {
        scrollWheelZoom: false,
        zoomControl: false
      }).setView(center, Number(root.dataset.zoom) || 8);

      L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19
        }
      ).addTo(map);

      const points = [];

      cards.forEach(card => {
        const point = coords(card);
        if (!point) return;

        const pin = document.createElement('span');
        pin.className = 'sl__pin';
        pin.setAttribute('aria-hidden', 'true');

        if (card.dataset.pinImage) {
          const image = document.createElement('img');
          image.src = card.dataset.pinImage;
          image.alt = '';
          image.loading = 'lazy';
          pin.appendChild(image);
        } else {
          pin.textContent = '●';
        }

        const icon = L.divIcon({
          className: '',
          html: pin.outerHTML,
          iconSize: [36, 36],
          iconAnchor: [18, 18]
        });

        const marker = L.marker(point, { icon }).addTo(map);
        const popup = document.createElement('div');
        popup.className = 'sl__popup';

        const title = document.createElement('strong');
        title.textContent = card.querySelector('h3').textContent;
        popup.appendChild(title);

        for (const selector of ['small', 'address', '.sl__hours']) {
          const source = card.querySelector(selector);

          if (source?.textContent.trim()) {
            const line = document.createElement('div');
            line.textContent = source.textContent.trim();
            popup.appendChild(line);
          }
        }

        const phone = card.querySelector('a[href^="tel:"]');
        if (phone) popup.appendChild(phone.cloneNode(true));

        const directions = card.querySelector('.sl__actions a[href*="maps"]');
        if (directions) popup.appendChild(directions.cloneNode(true));

        marker.bindPopup(popup, { maxWidth: 260, autoPan: false });

        marker.on('click', () => {
          select(card, true);
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });

        markers.set(card, marker);
        points.push(point);
      });

      if (points.length > 1) {
        map.fitBounds(points, { padding: [35, 35], maxZoom: 12 });
      } else if (points.length === 1) {
        map.setView(points[0], 12);
      }

      update();
    }

    const mapWrap = root.querySelector('.sl__map-wrap');

    root.querySelectorAll('[data-map-action]').forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        if (!map) return;

        const action = button.dataset.mapAction;

        if (action === 'zoom-in') {
          map.zoomIn();
        } else if (action === 'zoom-out') {
          map.zoomOut();
        } else if (action === 'up') {
          map.panBy([0, -130]);
        } else if (action === 'down') {
          map.panBy([0, 130]);
        } else if (action === 'left') {
          map.panBy([-130, 0]);
        } else if (action === 'right') {
          map.panBy([130, 0]);
        } else if (action === 'fullscreen') {
          mapWrap.classList.toggle('is-map-expanded');

          button.setAttribute(
            'aria-label',
            mapWrap.classList.contains('is-map-expanded')
              ? 'Cerrar pantalla completa'
              : 'Pantalla completa'
          );

          setTimeout(() => map.invalidateSize(), 80);
        } else if (action === 'fit') {
          if (active) active.classList.remove('is-active');

          active = null;
          map.closePopup();

          markers.forEach((marker, card) => {
            marker.setOpacity(card.hidden ? 0 : 1);
          });

          const visible = visibleCards().map(coords).filter(Boolean);

          if (visible.length > 1) {
            map.fitBounds(visible, { padding: [35, 35], maxZoom: 12 });
          } else if (visible.length === 1) {
            map.setView(visible[0], 13);
          }
        }
      });
    });

    root.addEventListener('keydown', event => {
      if (
        event.key === 'Escape' &&
        mapWrap.classList.contains('is-map-expanded')
      ) {
        mapWrap.classList.remove('is-map-expanded');
        setTimeout(() => map?.invalidateSize(), 80);
      }
    });

    function waitForLeaflet(tries = 0) {
      if (window.L) {
        mountMap();
      } else if (tries < 40) {
        setTimeout(() => waitForLeaflet(tries + 1), 250);
      } else {
        root.querySelector('[data-map-error]').hidden = false;
      }
    }

    root._locatorSelect = select;
    update();
    waitForLeaflet();

    root._locatorCleanup = () => {
      root.dataset.locatorReady = '';
      map?.remove();
    };
  }

  function boot() {
    document.querySelectorAll('.sl').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', event => {
    const root = event.target.matches?.('.sl')
      ? event.target
      : event.target.querySelector?.('.sl');

    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.matches?.('.sl')
      ? event.target
      : event.target.querySelector?.('.sl');

    root?._locatorCleanup?.();
  });

  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest?.('.sl__card');

    if (card) {
      card.hidden = false;
      card.closest('.sl')?._locatorSelect?.(card);
    }
  });

  setInterval(() => {
    document
      .querySelectorAll('.sl[data-locator-ready="true"]')
      .forEach(root => {
        root.querySelector('[data-search]')
          ?.dispatchEvent(new Event('input'));
      });
  }, MINUTE);
})();
